import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAchievementToast } from "@/components/achievements/AchievementToast";
import { useQueryClient } from "@tanstack/react-query";

export function useAchievementNotifications() {
  const { showAchievementToast } = useAchievementToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    // Buscar usuário atual
    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      // Subscrever a mudanças na tabela user_achievements
      channel = supabase
        .channel(`achievement-notifications:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'user_achievements',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            const newAchievement = payload.new;
            
            // Se foi desbloqueada (unlocked_at não é null)
            if (newAchievement.unlocked_at) {
              // Buscar detalhes da conquista
              const { data: achievement } = await supabase
                .from('achievements')
                .select('*')
                .eq('id', newAchievement.achievement_id)
                .maybeSingle();

              if (achievement) {
                // Mostrar toast de conquista desbloqueada
                showAchievementToast({
                  name: achievement.name,
                  description: achievement.description,
                  icon: achievement.icon,
                  points: achievement.points,
                  badge_color: achievement.badge_color,
                });

                // Invalidar queries para atualizar UI
                queryClient.invalidateQueries({ queryKey: ['user-achievements'] });
                queryClient.invalidateQueries({ queryKey: ['user_points'] });
              }
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_achievements',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            const oldAchievement = payload.old;
            const newAchievement = payload.new;
            
            // Se acabou de ser desbloqueada (old.unlocked_at era null e new.unlocked_at não é)
            if (!oldAchievement.unlocked_at && newAchievement.unlocked_at) {
              // Buscar detalhes da conquista
              const { data: achievement } = await supabase
                .from('achievements')
                .select('*')
                .eq('id', newAchievement.achievement_id)
                .maybeSingle();

              if (achievement) {
                // Mostrar toast de conquista desbloqueada
                showAchievementToast({
                  name: achievement.name,
                  description: achievement.description,
                  icon: achievement.icon,
                  points: achievement.points,
                  badge_color: achievement.badge_color,
                });

                // Invalidar queries para atualizar UI
                queryClient.invalidateQueries({ queryKey: ['user-achievements'] });
                queryClient.invalidateQueries({ queryKey: ['user_points'] });
              }
            }
          }
        )
        .subscribe();
    };

    void setupRealtimeSubscription();

    return () => {
      cancelled = true;
      if (channel) {
        void supabase.removeChannel(channel);
      }
    };
  }, [showAchievementToast, queryClient]);
}
